import React, { useContext, useEffect, useState } from 'react';
import { CircularProgress, makeStyles, TextField } from '@material-ui/core';
import AreaSelect from '../../components/AreaSelect';
import CustomDatePicker from '../../components/UI/CustomDatePicker';
import SelectAcerBrag from '../../components/SelectAcerBrag';
import DialogAcerBrag from '../../components/UI/DialogAcerBrag';
import RoleSelect, { ROLES_TYPES } from '../../components/RoleSelect';
import CountrySelect from '../../components/CountrySelect';
import userAPI from '../../services/UserApi';
import LimitedBackdrop from '../../components/UI/LimitedBackdrop';
import { AppContext } from '../../contexts/AppContext';
import { isValidEmail } from '../../utils/MiscellaneousUtils';
import RegionSelect from '../../components/RegionSelect';
import TreasuryGroupsSelect from '../../components/TreasuryGroupsSelect';
import { separations } from '../../constants/StyleConstants';
import { InventoriesContext } from '../../contexts/InventoriesContext';
import { Fragment } from 'react';
import CreateUserPasswordModal from './CreateUserPasswordModal';

const useStyles = makeStyles(theme => ({
  ...separations,
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '30em',
  },
  contiguous: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridGap: '1em',
  },
}));

const CreateUserDialog = ({ open, setOpen, readOnly, userId, isSupplierUser, reloadGrid }) => {
  const classes = useStyles();
  const { showError, showWarning, showSuccess } = useContext(AppContext);
  const { getTheRoles } = useContext(InventoriesContext);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [treasuryGroup, setTreasuryGroup] = useState('');
  const [password, setPassword] = useState('');
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [critico, setCritico] = useState(false);

  const resetFields = () => {
    setName('');
    setId('');
    setEmail('');
    setArea('');
    setSelectedDate(new Date());
    setStatus('');
    setRole('');
    setCountry('');
    setRegion('');
    setTreasuryGroup('');
    setPassword('');
    setCritico(false);
  };

  useEffect(() => {
    if (open && userId) {
      setOpenBackDrop(true);
      userAPI.getUserById(userId).then(res => {
        setName(res.fullName);
        setId(isSupplierUser ? res.supplierId : res.userName);
        setEmail(res.email);
        setArea(res.idArea || '');
        setSelectedDate(res.startDate);
        setStatus(res.state);
        setRole(res.roleId);
        setCountry(res.countryId || '');
        setRegion(res.regionId || '');
        setTreasuryGroup(res.treasuryGroupId || '');
        setOpenBackDrop(false);
        setCritico(res.critico);
      });
    } else {
      setTimeout(() => {
        resetFields();
      }, 500);
    }
  }, [open, userId]);

  const finish = () => {
    setOpen(false);
    setTimeout(() => {
      resetFields();
      setOpenBackDrop(false);
    }, 500);
  };

  const updateUser = async () => {
    const roles = await getTheRoles();
    const roleName = roles.filter(r => r.id === role)[0].name;

    const request = {
      IdArea: !isSupplierUser ? area : undefined,
      RoleName: roleName,
      State: status,
      Critico: critico,
    };

    userAPI
      .saveUser(userId, request)
      .then(result => {
        reloadGrid();
        finish();
        showSuccess('Usuario actualizado con éxito');
      })
      .catch(error => {
        finish();
        showError('No se pudo actualizar los datos de usuario');
      });
  };

  const newUser = async () => {
    const roles = await getTheRoles();
    const roleName = roles.filter(r => r.id === role)[0].name;

    const request = {
      UserName: id,
      Email: email,
      FullName: name,
      IdArea: area,
      RoleName: roleName,
      PwdUserLogged: password,
      Critico: critico,
    };

    userAPI
      .newUser(request)
      .then(result => {
        reloadGrid();
        finish();
        showSuccess('Usuario creado con éxito');
      })
      .catch(error => {
        finish();
        showError('No se pudo crear el nuevo usuario');
      });
  };

  const handleSave = () => {
    setOpenBackDrop(true);

    if (userId) {
      updateUser();
    } else {
      setOpenPasswordModal(true);
    }
  };

  const validate = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = name.isBlank())) {
      errorMessages.push('Debe completar el nombre del usuario');
    }

    if ((errors[i++] = id.isBlank())) {
      errorMessages.push('Debe completar el ID del usuario');
    }

    if ((errors[i++] = email.isBlank())) {
      errorMessages.push('Debe completar el email del usuario');
    } else if ((errors[i++] = !isValidEmail(email))) {
      errorMessages.push('El email ingresado no es válido');
    }

    if ((errors[i++] = area.isBlank() && !userId)) {
      errorMessages.push('Debe completar el área a la que pertenece el usuario');
    }

    if ((errors[i++] = role.isBlank())) {
      errorMessages.push('Debe completar el rol al que pertenece el usuario');
    }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateAndSave = () => validate() && handleSave();

  return (
    <Fragment>
      <CreateUserPasswordModal
        open={openPasswordModal}
        setOpen={setOpenPasswordModal}
        password={password}
        setPassword={setPassword}
        onCancel={() => {
          setOpenPasswordModal(false);
          setOpenBackDrop(false);
        }}
        newUser={newUser}
      />
      <DialogAcerBrag
        open={open}
        setOpen={setOpen}
        title={`${readOnly ? 'Detalles de' : userId ? 'Editar' : 'Nuevo'} usuario`}
        action={!readOnly ? validateAndSave : undefined}>
        <LimitedBackdrop open={openBackDrop}>
          <CircularProgress color="primary" />
        </LimitedBackdrop>
        <div className={classes.dialogContainer}>
          <TextField
            fullWidth
            margin="dense"
            id="razonSocialNombreYApellido"
            label="Nombre"
            onChange={e => setName(e.target.value)}
            value={name}
            inputProps={{ readOnly: readOnly || userId }}
          />
          <TextField
            fullWidth
            margin="dense"
            id="idDeAD"
            label="Id"
            onChange={e => setId(e.target.value)}
            value={id}
            inputProps={{ readOnly: readOnly || userId }}
          />
          <TextField
            margin="dense"
            id="email"
            label="Email"
            onChange={e => setEmail(e.target.value)}
            value={email}
            inputProps={{ readOnly: readOnly || userId }}
          />
          <div className={classes.separationBetween}>
            <CustomDatePicker readOnly value={selectedDate} setValue={setSelectedDate} />
            <SelectAcerBrag
              stretch={readOnly}
              readOnly={readOnly || !userId}
              rows={[
                { id: true, name: 'Activo' },
                { id: false, name: 'Inactivo' },
              ]}
              value={!userId ? true : status}
              setValue={setStatus}
              selectTitle="Estado"
            />
          </div>
          {!isSupplierUser && (
            <AreaSelect disableOptionAll readOnly={readOnly} style={{ marginTop: 8 }} value={area} setValue={setArea} />
          )}
          <RoleSelect
            variableWidth={readOnly}
            readOnly={readOnly}
            value={role}
            setValue={setRole}
            showOnly={isSupplierUser ? ROLES_TYPES.SUPPLIERS : ROLES_TYPES.USERS}
          />
          {isSupplierUser && (
            <div className={classes.contiguous}>
              <CountrySelect matchById readOnly variableWidth={'100%'} value={country} setValue={setCountry} />
              <RegionSelect matchById readOnly variableWidth={'100%'} value={region} setValue={setRegion} />
            </div>
          )}
          {isSupplierUser && <TreasuryGroupsSelect matchById readOnly variableWidth value={treasuryGroup} setValue={setTreasuryGroup} />}
          {isSupplierUser ? <SelectAcerBrag
              stretch={readOnly}
              readOnly={readOnly || !userId}
              rows={[
                { id: false, name: 'No' },
                { id: true, name: 'Si' },
              ]}
              value={!userId ? false : critico}
              setValue={setCritico}
              selectTitle="Critico"
            /> : ''}
        </div>
      </DialogAcerBrag>
    </Fragment>
  );
};
export default CreateUserDialog;
